import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, LogOut, Menu, Monitor, Moon, Settings2, Sun, UserCircle2, X } from 'lucide-react';
import WindowControls from './WindowControls';
import { StatusBarGlobal } from './layout/StatusBarGlobal';
import { EnterpriseSidebar } from './layout/EnterpriseSidebar';
import { RibbonProvider, useRibbon } from '../context/RibbonContext';
import { ReportProvider, useReport } from '../context/ReportContext';
import { ReportStudioOverlay } from './ReportStudioOverlay';
import OperationalBreadcrumb from './operational/OperationalBreadcrumb';
import { useOperationalContext } from '../contexts/OperationalContext';
import { getNavigationRouteMeta } from './layout/navigationRegistry';
import { LauncherDrawer } from './launcher/LauncherDrawer';
import { useProjectContext } from '../context/ProjectContext';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useGamification } from '../hooks/useGamification';
import { useIsMobile } from '../hooks/useMediaQuery';
import { OrganizationSelector } from './ui/OrganizationSelector';

export const WorkspaceLayout: React.FC = () => {
  return (
    <RibbonProvider>
      <ReportProvider>
        <WorkspaceLayoutInner />
        <ReportStudioOverlay />
      </ReportProvider>
    </RibbonProvider>
  );
};

const WorkspaceLayoutInner: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedProject } = useProjectContext();
  const { ribbonContent, headerMetaContent } = useRibbon();
  const { isReportMode } = useReport();
  const { activeExecutionId, activeStageId } = useOperationalContext();
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const { userPoints } = useGamification();
  const isMobile = useIsMobile();

  const authorityRingClass = (() => {
    switch (userPoints?.authority_level ?? 'bronze') {
      case 'bronze':     return 'ring-1 ring-orange-500/70 dark:ring-orange-500/60';
      case 'silver':     return 'ring-1 ring-slate-400/80 dark:ring-slate-400/70';
      case 'gold':       return 'ring-1 ring-amber-500/80 dark:ring-yellow-400/70';
      case 'black_belt': return 'ring-1 ring-purple-500/80 dark:ring-purple-400/70';
      default:           return 'ring-1 ring-orange-500/70 dark:ring-orange-500/60';
    }
  })();

  const [isElectron, setIsElectron] = useState(false);
  const [immersiveSidebarOpen, setImmersiveSidebarOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [isYamazumiFocusMode, setIsYamazumiFocusMode] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const routeMeta = getNavigationRouteMeta(location.pathname);
  const isMinhaMesaRoute = location.pathname.startsWith('/minha-mesa');
  const isExecucoesRoute = location.pathname.startsWith('/execucoes');
  const isAcoesRoute = location.pathname.startsWith('/acoes') || location.pathname.startsWith('/plano');
  // Only the VSM map editor uses an active project as the topbar primary context.
  const isMapaRoute = location.pathname.startsWith('/mapa');

  useEffect(() => {
    setIsElectron(!!(window.windowAPI ?? window.electron));
  }, []);

  useEffect(() => {
    if (routeMeta.immersive) {
      setImmersiveSidebarOpen(false);
    }
  }, [routeMeta.immersive]);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const syncSidebarWidth = () => {
      const narrow = window.innerWidth < 768;
      if (narrow && !mobileNavOpen) {
        document.documentElement.style.setProperty('--sidebar-effective-width', '0px');
      }
    };
    syncSidebarWidth();
    window.addEventListener('resize', syncSidebarWidth);
    window.addEventListener('orientationchange', syncSidebarWidth);
    return () => {
      window.removeEventListener('resize', syncSidebarWidth);
      window.removeEventListener('orientationchange', syncSidebarWidth);
    };
  }, [mobileNavOpen]);

  useEffect(() => {
    if (!mobileNavOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMobileNavOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mobileNavOpen]);

  useEffect(() => {
    const handleFocusModeChange = (event: Event) => {
      const detail = (event as CustomEvent<{ active?: boolean }>).detail;
      setIsYamazumiFocusMode(Boolean(detail?.active));
    };

    window.addEventListener('yamazumi-focus-mode-change', handleFocusModeChange as EventListener);
    return () => window.removeEventListener('yamazumi-focus-mode-change', handleFocusModeChange as EventListener);
  }, []);

  useEffect(() => {
    if (!userMenuOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setUserMenuOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [userMenuOpen]);

  const showOperationalBreadcrumb =
    routeMeta.breadcrumb &&
    !isExecucoesRoute &&
    Boolean(activeExecutionId || activeStageId);
  const projectName = useMemo(
    () => selectedProject?.nome_projeto || 'Workspace',
    [selectedProject?.nome_projeto],
  );
  const shouldRenderRibbonContainer = routeMeta.hasRibbon;
  const userLabel = user?.name?.trim() || user?.email?.split('@')[0] || 'Usuario';

  const handleThemeChange = (nextTheme: 'light' | 'midnight' | 'black') => {
    setTheme(nextTheme);
    setUserMenuOpen(false);
  };

  const handleSettings = () => {
    navigate('/account/preferences');
    setUserMenuOpen(false);
  };

  const handleLogout = async () => {
    await logout();
    setUserMenuOpen(false);
    navigate('/', { replace: true });
  };

  return (
    <div className={`enterprise-shell-root enterprise-shell-bg enterprise-font relative flex h-screen w-full overflow-hidden ${isMobile ? '' : 'select-none'}`}>
      {/* Sidebar fixa — tablet/desktop (CSS + Tailwind) */}
      <div className="enterprise-shell-sidebar-desktop hidden shrink-0 md:block">
        <EnterpriseSidebar
          immersive={routeMeta.immersive}
          immersiveOpen={routeMeta.immersive ? immersiveSidebarOpen : true}
          focusMode={isYamazumiFocusMode || isReportMode}
        />
      </div>

      {/* Drawer mobile — abre só com mobileNavOpen; oculto em md+ via CSS */}
      {mobileNavOpen && (
        <div className="enterprise-shell-mobile-drawer md:hidden">
          <button
            type="button"
            className="fixed inset-0 z-[60] bg-black/45"
            aria-label="Fechar menu"
            onClick={() => setMobileNavOpen(false)}
          />
          <EnterpriseSidebar
            immersive
            immersiveOpen
            focusMode={false}
            onNavigate={() => setMobileNavOpen(false)}
          />
        </div>
      )}

      <section
        className="enterprise-shell-section flex min-w-0 flex-1 flex-col overflow-hidden"
        style={{ paddingBottom: 'var(--shell-statusbar-height, 24px)' }}
      >
        <header
          className={`titlebar enterprise-shell-header relative z-30 flex h-[var(--shell-header-height)] shrink-0 select-none items-center ${routeMeta.immersive ? 'z-[80]' : ''}`}
          onDoubleClick={isElectron ? () => window.windowAPI?.maximize?.() : undefined}
        >
          <div className="no-drag flex min-w-0 flex-1 items-center text-sm">
            <button
              type="button"
              onClick={() => setMobileNavOpen((prev) => !prev)}
              className="enterprise-shell-mobile-menu-btn mr-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-sm text-[var(--text-secondary)] transition-colors hover:bg-[var(--button-hover)] md:hidden"
              aria-label={mobileNavOpen ? 'Fechar menu' : 'Abrir menu'}
              aria-expanded={mobileNavOpen}
            >
              {mobileNavOpen ? <X size={16} strokeWidth={1.75} /> : <Menu size={16} strokeWidth={1.75} />}
            </button>
            <div
              className="hidden shrink-0 md:block"
              style={{ width: 'var(--shell-header-inset, var(--shell-content-inset, 10px))' }}
              aria-hidden="true"
            />
            <div className="flex min-w-0 flex-row items-baseline gap-2">
              <span className="shrink-0 text-[12px] font-semibold leading-none uppercase tracking-[0.06em] text-enterprise-text-primary dark:text-slate-200">
                {routeMeta.title}
              </span>
              {headerMetaContent && (
                <>
                  <span className="shrink-0 text-[11px] font-normal leading-none text-[var(--text-secondary)] mx-0.5">·</span>
                  <div className="min-w-0 flex items-baseline gap-1.5 text-[11px] font-normal leading-none text-[var(--text-secondary)]">
                    {headerMetaContent}
                  </div>
                </>
              )}
            </div>
            {showOperationalBreadcrumb && (
              <>
                <span className="shrink-0 px-1 text-[11px] font-normal leading-none text-[var(--text-secondary)]">·</span>
                <OperationalBreadcrumb
                  className="min-w-0"
                  mode={isMinhaMesaRoute ? 'execution_only' : 'full'}
                />
              </>
            )}
          </div>

          <div className="flex-1" />

          <div
            className="no-drag flex items-center gap-1"
            style={{ paddingRight: 'var(--shell-header-inset, var(--shell-content-inset, 10px))' }}
          >
            <OrganizationSelector variant="compact" className="mr-1" />
            {user && (
              <div ref={userMenuRef} className="relative mr-1">
                <button
                  type="button"
                  onClick={() => setUserMenuOpen((prev) => !prev)}
                  className="flex h-6 items-center gap-1.5 rounded-sm border border-transparent px-1.5 text-[11px] font-normal text-[var(--text-secondary)] transition-colors hover:bg-[var(--button-hover)]"
                  aria-haspopup="menu"
                  aria-expanded={userMenuOpen}
                >
                  {user.avatar_url ? (
                    <div className={`rounded-full p-[1.5px] ${authorityRingClass}`}>
                      <img
                        src={user.avatar_url}
                        alt={userLabel}
                        className="h-[18px] w-[18px] rounded-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className={`rounded-full p-[1px] ${authorityRingClass}`}>
                      <UserCircle2 size={14} strokeWidth={1.5} className="text-[var(--text-secondary)]" />
                    </div>
                  )}
                  <span className="max-w-[120px] truncate max-[1180px]:hidden">{userLabel}</span>
                  <ChevronDown size={11} strokeWidth={1.5} className="opacity-55" />
                </button>

                {userMenuOpen && (
                  <div className="absolute right-0 top-full z-[90] mt-1.5 w-[200px] overflow-hidden rounded-[3px] border border-[var(--border-default)] bg-[var(--bg-surface)] py-1.5 text-[var(--text-primary)] shadow-none">
                    <div className="px-2.5 pb-1 pt-1 text-[11px] font-semibold uppercase leading-[1.1] tracking-[0.05em] text-[var(--text-secondary)]">
                      Tema
                    </div>
                    <button
                      type="button"
                      onClick={() => handleThemeChange('light')}
                      className={`flex h-[26px] w-full items-center gap-2 border-l-[3px] px-2.5 py-[3px] text-left text-[13px] leading-[1.2] transition-colors duration-100 ${
                        theme === 'light'
                          ? 'border-l-[#4A84E0] bg-[color-mix(in_srgb,var(--brand-primary)_14%,var(--bg-surface))] text-[var(--text-primary)] font-medium'
                          : 'border-l-transparent text-[var(--text-primary)] font-normal hover:bg-[var(--button-hover)]'
                      }`}
                    >
                      <Sun size={13} strokeWidth={1.5} />
                      Light
                    </button>
                    <button
                      type="button"
                      onClick={() => handleThemeChange('midnight')}
                      className={`flex h-[26px] w-full items-center gap-2 border-l-[3px] px-2.5 py-[3px] text-left text-[13px] leading-[1.2] transition-colors duration-100 ${
                        theme === 'midnight'
                          ? 'border-l-[#4A84E0] bg-[color-mix(in_srgb,var(--brand-primary)_14%,var(--bg-surface))] text-[var(--text-primary)] font-medium'
                          : 'border-l-transparent text-[var(--text-primary)] font-normal hover:bg-[var(--button-hover)]'
                      }`}
                    >
                      <Moon size={13} strokeWidth={1.5} />
                      Midnight
                    </button>
                    <button
                      type="button"
                      onClick={() => handleThemeChange('black')}
                      className={`flex h-[26px] w-full items-center gap-2 border-l-[3px] px-2.5 py-[3px] text-left text-[13px] leading-[1.2] transition-colors duration-100 ${
                        theme === 'black'
                          ? 'border-l-[#4A84E0] bg-[color-mix(in_srgb,var(--brand-primary)_14%,var(--bg-surface))] text-[var(--text-primary)] font-medium'
                          : 'border-l-transparent text-[var(--text-primary)] font-normal hover:bg-[var(--button-hover)]'
                      }`}
                    >
                      <Monitor size={13} strokeWidth={1.5} />
                      Black
                    </button>
                    <div className="my-1 h-px bg-[var(--border-subtle)]" />
                    <button
                      type="button"
                      onClick={handleSettings}
                      className="flex h-[26px] w-full items-center gap-2 border-l-[3px] border-l-transparent px-2.5 py-[3px] text-left text-[13px] font-normal leading-[1.2] text-[var(--text-primary)] transition-colors duration-100 hover:bg-[var(--button-hover)]"
                    >
                      <Settings2 size={13} strokeWidth={1.5} />
                      Preferências
                    </button>
                    <div className="my-1 h-px bg-[var(--border-subtle)]" />
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="flex h-[26px] w-full items-center gap-2 border-l-[3px] border-l-transparent px-2.5 py-[3px] text-left text-[13px] font-normal leading-[1.2] text-red-500 dark:text-red-400 transition-colors duration-100 hover:bg-[var(--button-hover)]"
                    >
                      <LogOut size={13} strokeWidth={1.5} />
                      Sair
                    </button>
                  </div>
                )}
              </div>
            )}
            <div className="ml-1 border-l border-enterprise-border-soft pl-1 dark:border-slate-700">
              <WindowControls />
            </div>
          </div>
        </header>

        {shouldRenderRibbonContainer && !isYamazumiFocusMode ? (
          <div className="relative z-20 shrink-0 bg-transparent" style={{ background: 'transparent', backgroundColor: 'transparent', backgroundImage: 'none' }}>
            {ribbonContent}
          </div>
        ) : null}

        <main
          className={`enterprise-shell-main enterprise-shell-bg min-h-0 flex-1 w-full ${
            isMobile ? 'overflow-y-auto overflow-x-hidden overscroll-y-contain' : 'overflow-hidden'
          }`}
        >
          <Outlet />
        </main>

      </section>

      <StatusBarGlobal className="fixed inset-x-0 bottom-0 z-10" />

      {/* Global Launcher Drawer (Prompt 31) */}
      <LauncherDrawer />
    </div>
  );
};
